import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridExpandMoreIcon,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { Dispatch, SetStateAction, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import HPOChipSelector from './HPOChipSelector';
import HPOFormControls from '../Patient/components/HPOFormControls';
import {
  HPOSchema,
  PatientHPOListsSchema,
  SavedFilterResults,
} from '@/zod_schemas';
import { useCreateHPO } from '@/api/hpo/hpo';

interface HPOFilterProps {
  setSelectedVariantRows: Dispatch<SetStateAction<GridRowSelectionModel>>;
  setOpen: Dispatch<SetStateAction<boolean>>;
  patientHpoList: PatientHPOListsSchema;
  open: boolean;

  searchQuery: string;
  handleSearchChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  handleSearchClick: (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.KeyboardEvent<HTMLDivElement>,
  ) => void;
  searchFieldRef: React.RefObject<HTMLInputElement>;
  focusSearchField: () => void;
  selectedVariantRows: GridRowSelectionModel;
  columnsResponse: GridColDef<HPOSchema>[];
  fetchedData:
    | {
        termData?: {
          terms: {
            id: string;
            hpo_id: string;
            descendant_count: number | null;
            synonyms: string[];
            xrefs: string[];
            translations: string | null;
            genes: { id: string; name: string }[]; // This should be an array
            name?: string;
            definition?: string;
            comment?: string;
            created_at?: string;
            updated_at?: string;
          }[];
          totalCount: number;
        } | null;
        geneData?: unknown;
        diseaseData?: unknown;
      }
    | {
        termData?: undefined;
        geneData?: undefined;
        diseaseData?: undefined;
      };
  refchPatientHPO: (options?: RefetchOptions | undefined) => Promise<
    QueryObserverResult<
      {
        hpo: {
          name: string;
          hpo_id: string;
          gene: string[];
        }[];
        patient_id: string;
      },
      Error
    >
  >;
}

const HPOFilter = ({
  setSelectedVariantRows,
  setOpen,
  patientHpoList,
  refchPatientHPO,
  open,
  searchQuery,
  handleSearchChange,
  handleSearchClick,
  searchFieldRef,
  focusSearchField,
  selectedVariantRows,
  columnsResponse,
  fetchedData,
}: HPOFilterProps) => {
  const { control } = useFormContext<SavedFilterResults>();

  const createHPO = useCreateHPO();
  const [hpoRowData, setHpoRowData] = useState<HPOSchema[]>([]);

  return (
    <Accordion>
      <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
        <Typography>HPO Filters</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              mb: 2,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setSelectedVariantRows([]); // Clear grid selection
                setOpen(true);
              }}
            >
              Browse HPO
            </Button>
          </Box>

          {/* Chip Selector */}
          <HPOChipSelector
            name="request_filters.filters.hpo_filters"
            control={control}
            patientHpoList={patientHpoList}
          />

          {/* Dialog for Browse */}
          <Dialog
            open={open}
            onClose={() => setOpen(false)}
            fullWidth
            maxWidth="lg"
            PaperProps={{
              sx: {
                bgcolor: 'background.default',
                border: 'none',
              },
            }}
          >
            <DialogContent sx={{ p: 3 }}>
              <Box>
                <Typography variant="h5" mb={2} color="primary">
                  Human Phenotype Ontology
                </Typography>
                {/* Buttons Row */}

                <Stack
                  direction="row"
                  spacing={2}
                  mb={2}
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                >
                  <Box sx={{ flexGrow: 1, maxWidth: '500px' }}>
                    <HPOFormControls
                      searchQuery={searchQuery}
                      handleSearchChange={handleSearchChange}
                      handleSearchClick={handleSearchClick}
                      inputRef={searchFieldRef}
                      focusSearchField={focusSearchField}
                    />
                  </Box>

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={async () => {
                        const patientId = patientHpoList.patient_id;

                        const validData = hpoRowData.map((hpo) => ({
                          ...hpo,
                          patient_id: patientId,
                        }));

                        try {
                          const result = await createHPO.mutateAsync(validData);

                          if (result?.data) {
                            enqueueSnackbar('HPO added successfully', {
                              variant: 'success',
                            });
                            setOpen(false);
                            refchPatientHPO();
                          } else {
                            enqueueSnackbar(
                              'Failed to retrieve valid result after creating HPOs',
                              { variant: 'error' },
                            );
                          }
                        } catch (ex) {
                          enqueueSnackbar('Failed to create HPOs', {
                            variant: 'error',
                          });
                          setHpoRowData([]);
                        }
                      }}
                    >
                      Select
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={() => {
                        setHpoRowData([]);
                        setOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Stack>

                <Box
                  sx={{
                    height: 400,
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    '& .MuiDataGrid-root': {
                      border: 'none',
                      fontFamily: 'Roboto, sans-serif',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'primary.light',
                      fontWeight: 'bold',
                    },
                  }}
                >
                  {/* DataGrid */}
                  <DataGrid
                    rows={fetchedData?.termData?.terms || []}
                    columns={columnsResponse}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 50 } },
                    }}
                    sx={{ height: 400 }}
                    pageSizeOptions={[3]}
                    checkboxSelection
                    disableRowSelectionOnClick
                    getRowId={(row) => row.hpo_id}
                    rowSelectionModel={selectedVariantRows}
                    // onRowSelectionModelChange={(newSelection) =>
                    //   setSelectedVariantRows(newSelection)
                    // }
                    onRowSelectionModelChange={(newSelectionModel) => {
                      const currentPageRows =
                        fetchedData?.termData?.terms || [];

                      // 1. Update selection state
                      setSelectedVariantRows(newSelectionModel);

                      // 2. Keep only rows from new selection
                      const updatedHpoRowData = currentPageRows.filter((row) =>
                        newSelectionModel.includes(row.hpo_id),
                      );

                      // 3. Merge with any existing data that wasn't on this page
                      const remainingOldRows = hpoRowData.filter(
                        (row) =>
                          !currentPageRows.some(
                            (pageRow) => pageRow.hpo_id === row.hpo_id,
                          ) && newSelectionModel.includes(row.hpo_id),
                      );

                      setHpoRowData([
                        ...remainingOldRows,
                        ...updatedHpoRowData,
                      ]);
                    }}
                  />
                </Box>
              </Box>
            </DialogContent>
          </Dialog>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
export default HPOFilter;
